/**
 * Canonical `data-amp-track-component` registry.
 *
 * Source of truth: `registry/amplitude-registry.json` (generated via `yarn sync:amp-registry`).
 * Specificity (nav item identity, tab names, table row types, etc.) belongs in
 * `data-amp-track-detail`, not in the component value.
 */

import { AMP_TRACK_COMPONENT_VALUES } from './generated/amp-track-components.generated';

export { AMP_TRACK_COMPONENT_ALIASES, AMP_TRACK_COMPONENT_DETAILS } from './generated/amp-track-components.generated';

export type AmpTrackComponentCategory =
  | 'controls'
  | 'navigation'
  | 'display'
  | 'actions';

export interface AmpTrackComponentRule {
  /** Canonical kebab-case value. */
  value: string;
  /** Logical grouping, used by the QA audit table. */
  category: AmpTrackComponentCategory;
  /** Optional contexts where this component is expected (used for reporting only). */
  contexts?: readonly string[];
}

/** @deprecated Use getKnownAmpTrackComponents() — kept for import compatibility. */
export const AMP_TRACK_COMPONENT_REGISTRY: readonly AmpTrackComponentRule[] = [...AMP_TRACK_COMPONENT_VALUES]
  .sort()
  .map(value => ({ value, category: 'actions' as AmpTrackComponentCategory }));

/** @deprecated Use AMP_TRACK_COMPONENT_REGISTRY — kept for import compatibility. */
export const NAV_AMP_TRACK_COMPONENTS: readonly AmpTrackComponentRule[] = [];

/** @deprecated Use AMP_TRACK_COMPONENT_REGISTRY — kept for import compatibility. */
export const SUBNAV_AMP_TRACK_COMPONENTS: readonly AmpTrackComponentRule[] = [];

/** @deprecated Use AMP_TRACK_COMPONENT_REGISTRY — kept for import compatibility. */
export const GENERIC_AMP_TRACK_COMPONENTS: readonly AmpTrackComponentRule[] = AMP_TRACK_COMPONENT_REGISTRY;

export const PROVISIONAL_AMP_TRACK_COMPONENTS: readonly AmpTrackComponentRule[] = [];

const PROVISIONAL_VALUES: ReadonlySet<string> = new Set(
  PROVISIONAL_AMP_TRACK_COMPONENTS.map(r => r.value),
);

/** True when the value is recognised in the canonical component registry. */
export function isKnownAmpTrackComponent(value: string | null | undefined): boolean {
  return value != null && AMP_TRACK_COMPONENT_VALUES.has(value);
}

/** True when the value is recognised but only as a *provisional* entry. */
export function isProvisionalAmpTrackComponent(value: string | null | undefined): boolean {
  return value != null && PROVISIONAL_VALUES.has(value);
}

/** Read-only view of all canonical component values. */
export function getKnownAmpTrackComponents(): ReadonlySet<string> {
  return AMP_TRACK_COMPONENT_VALUES;
}

/** Read-only view of provisional component values. */
export function getProvisionalAmpTrackComponents(): ReadonlySet<string> {
  return PROVISIONAL_VALUES;
}
